/**
 * Just a plain exception, but with a unique type so you know where it came
 * from when you catch it.
 */
export class MeshWriterError extends Error { }

export type TypedArray= Int8Array | Uint8Array | Uint8ClampedArray | Int16Array
    | Uint16Array | Int32Array | Uint32Array | Float32Array | Float64Array;

export type TypedArrayConstructor =
        typeof Int8Array
        | typeof Uint8Array
        | typeof Uint8ClampedArray
        | typeof Int16Array
        | typeof Uint16Array
        | typeof Int32Array
        | typeof Uint32Array
        | typeof Float32Array
        | typeof Float64Array
        ;

/**
* The Attr class describes attributes of buffer geometry. This is related to
* the three.js BufferAttribute class, but in this case it doesn't store the
* data or implement operations on that data. Further, we make the strong
* assumption that the data is never normalized integer data, or that if it is,
* then the underlying operations will deal with that knowledge based on the
* attribute name, e.g. if we're dealing with vertext colors which are in some
* sense normalized data.
* @param {string} name - The attribute name. "position" for position, "uv" for
* UV coordinates, "normal" for vertex normals, or "color" for vertex colors.
* @param {number} size - The number of array elements that each vertx
* occupies. For UVs this is 2, and for the other standard attributes it's 3.
* @param {number} count - How many elements are in the underlying type array.
* The length of the typed array will be size*count. The byte length of the
* underlying array buffer will be size*count*type.BYTES_PER_ELEMENT.
* @param {Function} type - The TypedArray constructor that pertains to the
* underlying data.
*/
export class Attr {
    name: string;
    size: number;
    count: number;
    type: TypedArrayConstructor;

    constructor(name: string, size: number, count: number, type: TypedArrayConstructor) {
        this.name = name;
        this.size = size;
        this.count = count;
        this.type = type;
    }

    get length() {
        return this.size * this.count;
    }

    get byteLength() {
        return this.size * this.count * this.type.BYTES_PER_ELEMENT;
    }
}

/**
 * The attribute map is a regular JavaScript Map object that also tracks
 * attributes by their names. In TypeScript generic syntax, this would be a
 * Map<Attr, TypedArray>. The byName method provides easy access to the
 * attribute and data in the attribute map.
 */
export class AttrMap extends Map<Attr, TypedArray> {
    nameMap: Map<string, [Attr, TypedArray]>;

    constructor() {
        super();
        this.nameMap = new Map();
    }

    set(attr: Attr, data: TypedArray) {
        super.set(attr, data);
        this.nameMap.set(attr.name, [attr, data]);
        return this;
    }

    byName(name: string) {
        return this.nameMap.get(name);
    }
}

/**
* The MeshWriter is an abstract base class that implements the generating
* procedure.
*/
export default abstract class MeshWriter {

    /**
     * Allocates array buffer space for each attribute and passes the
     * appropriate arrays to the implementer's `write` method.
     * @returns A mapping from attributes to their
     * binary data represented as TypedArray instances. Note that they all point
     * to a single contiguous byte buffer in memory.
    */
    generate(): AttrMap {
        const attrMap = new AttrMap();
        let totalSize = 0;
        const attrOffsets = new Map();
        const attrLengths = new Map();
        for(const attr of this.attrs) {
            attrOffsets.set(attr, totalSize);
            attrLengths.set(attr, attr.length);
            totalSize += attr.byteLength;
        }

        // Memory allocation is the biggest performance hit we can sustain, so
        // allocating all the memory for every operation we're about to do ...
        // all at once ... is supposed to be our big performance win with this
        // architecture.
        const buffer = new ArrayBuffer(totalSize);

        for(const attr of attrOffsets.keys()) {
            const offset = attrOffsets.get(attr);
            const len = attrLengths.get(attr);
            const data = new attr.type(buffer, offset, len);
            attrMap.set(attr, data);
        }

        this.write(attrMap);
        return attrMap;
    }

    /**
     * Implementing classes should override the `attrs` method.
     * @returns the set of attributes with size and type data.
     */
    abstract get attrs(): Set<Attr>;

    /**
     * Implementing classes should override the write method.
     * @param attrMap - the attributes returned by the attrs property mapped
     * onto TypedArray instances that can received the data. Note that the
     * `generate` implementation uses a single ArrayBuffer to consolidate all
     * the TypedArray instances into one contiguous memory block.
     */
    abstract write(attrMap: AttrMap): void;

}
